import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';
import { ensureSignupConversionFired } from '@/lib/signupConversion';
import { commitAttributionToProfile } from '@/lib/gclidCapture';

const SIGNUP_CONFIRMING_KEY = 'fab_signup_confirming';

/**
 * /welcome — fires the sign_up conversion exactly once per user.
 *
 * Onboarding-exempt but auth-gated. Sits between MFA verification and
 * /connect-jotform. Flag set in profiles.signup_tracked_at guards against
 * re-fires on reload or repeat visits. Per plan Q6, the flag is written
 * IMMEDIATELY on event dispatch (before render completes) so a tab close
 * + reload cycle can't re-fire.
 */
const Welcome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    (async () => {
      try {
        // Also opportunistically commit any captured attribution to profile.
        // Safe to call repeatedly; it's a no-op once profile.gclid is set.
        await commitAttributionToProfile(supabase, user.id);
        if (cancelled) return;

        // Single source of truth — atomic, race-safe, deduped against the
        // PrivateRoute fire and any other tab. No-ops if already tracked.
        await ensureSignupConversionFired(supabase, user.id, user.email ?? undefined);
        if (cancelled) return;

        // Clear the signup confirmation marker so future navigations don't
        // re-route back here. The profile flag remains as the canonical guard.
        try {
          sessionStorage.removeItem(SIGNUP_CONFIRMING_KEY);
        } catch {
          // sessionStorage may be unavailable in some embedded contexts — ignore.
        }

        setIsReady(true);
      } catch (err) {
        if (cancelled) return;
        logger.error('Welcome: unexpected error', err);
        setIsReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const onContinue = () => {
    navigate('/connect-jotform', { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-grow container mx-auto px-4 py-12 flex items-center justify-center">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Welcome to FAB</CardTitle>
            <CardDescription>
              Your account is live. Next: connect your Jotform account so FAB can automatically back up your forms.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <Button onClick={onContinue} size="lg" disabled={!isReady}>
              {isReady ? 'Continue' : <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading…</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Welcome;
